import { format } from 'date-fns/format';
import { isValid } from 'date-fns/isValid';
import { LOOKBOOK_FAMILY, type LookbookFamily } from '@/features/lookbooks/family';
import { LinkedBy } from '@/features/lookbooks/kinds/pages/linkedBy';

/*
  ALS-77: words for the top card and the category branch, taken verbatim from the prototype
  (screens/lookbook-new.html, scripts/screens/lookbook-new.js) where it has them. The middle
  card's real content (pages read from a PDF, media uploaded, products picked) is a separate
  ticket, so only its empty-state copy lives here.
*/

/** The audit line's own date shape - "03 Jun 2026", never the API's raw ISO instant. */
function auditDate(iso: string | null): string | null {
    if (!iso) return null;
    const day = new Date(iso);
    return isValid(day) ? format(day, 'dd MMM yyyy') : null;
}

/* The register's words (ALS-75, ALS-76), verbatim from screens/lookbooks.html where it has them. */
const plural = (n: number, one: string, many: string) =>
    `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;

const pages = (n: number) => plural(n, 'page', 'pages');
const photos = (n: number) => plural(n, 'photo', 'photos');
const videos = (n: number) => plural(n, 'video', 'videos');
const products = (n: number) => plural(n, 'product', 'products');

/** "12 pages", "15 photos", "1 video", "8 products": what a book of this family holds. */
function contentCount(family: LookbookFamily, n: number): string {
    if (family === LOOKBOOK_FAMILY.photos) return photos(n);
    if (family === LOOKBOOK_FAMILY.video) return videos(n);
    if (family === LOOKBOOK_FAMILY.products) return products(n);
    return pages(n);
}

/** "goes" for one, "go" for many: the verb agrees with the count in front of it. */
const agree = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * The delete sentence's middle: a product book's products stay in the catalogue, a page book's
 * pages go while the products on them stay, and a media book's files go with it. A count of
 * nothing is never spelt out as "0": an empty book says only the book is removed.
 */
function deleteFate(family: LookbookFamily, content: number, productCount: number): string {
    if (family === LOOKBOOK_FAMILY.products)
        return productCount === 0
            ? 'Only the book is removed.'
            : `Its ${products(productCount)} ${agree(productCount, 'stays', 'stay')} in the catalogue; only the book is removed.`;
    if (content === 0)
        return family === LOOKBOOK_FAMILY.pages && productCount > 0
            ? `Only the book is removed; the ${products(productCount)} in it ${agree(productCount, 'stays', 'stay')} in the catalogue.`
            : 'Only the book is removed.';
    const goes = `Its ${contentCount(family, content)} ${agree(content, 'goes', 'go')} with it`;
    if (family !== LOOKBOOK_FAMILY.pages || productCount === 0) return `${goes}.`;
    return `${goes}; the ${products(productCount)} on ${agree(content, 'it', 'them')} ${agree(productCount, 'stays', 'stay')} in the catalogue.`;
}

export const COPY = {
    eyebrow: 'Lookbooks',
    title: 'Lookbooks',
    cardTitle: 'Lookbooks',
    searchPlaceholder: 'Search lookbooks…',
    searchLabel: 'Search lookbooks',
    add: 'New lookbook',
    export: 'Export',
    exported: (n: number) => `Exported · ${plural(n, 'lookbook', 'lookbooks')}`,
    exportFailed: 'The export could not be prepared.',
    loading: 'Loading lookbooks',
    /** The card's count: the matched set only, never "of M". */
    count: (n: number) => plural(n, 'lookbook', 'lookbooks'),
    /** The filter bar's count: what the filters matched, of the whole register. */
    showing: (matched: number, register: number) =>
        `Showing ${matched.toLocaleString('en-GB')} of ${register.toLocaleString('en-GB')} lookbooks`,
    /** The footer's range of the matched set. */
    range: (from: number, to: number, total: number) =>
        `Showing ${from.toLocaleString('en-GB')}–${to.toLocaleString('en-GB')} of ${plural(total, 'lookbook', 'lookbooks')}`,
    clearAll: 'Clear all',
    /** A book with no client, and a book never revised. */
    none: 'NA',
    columns: {
        lookbook: 'Lookbook',
        category: 'Category',
        customer: 'Customer',
        effective: 'Effective',
        content: 'Content',
        createdBy: 'Created by',
        revised: 'Revised',
        active: 'Active',
    },
    yes: 'Yes',
    no: 'No',
    content: {
        count: contentCount,
        pages,
        photos,
        videos,
        products,
        /** The line under the book's name. */
        line: (family: LookbookFamily, content: number, productCount: number): string => {
            if (family === LOOKBOOK_FAMILY.photos) return `Photo book · ${photos(content)}`;
            if (family === LOOKBOOK_FAMILY.video) return 'Video book';
            if (family === LOOKBOOK_FAMILY.products)
                return `Product book · ${products(productCount)}`;
            return `${pages(content)} · ${products(productCount)}`;
        },
    },
    failed: {
        title: 'Lookbooks could not be loaded',
        body: 'A problem reading the data. Nothing has been lost, and the books already in the client app are unaffected.',
        action: 'Try again',
    },
    empty: {
        title: 'No lookbooks yet',
        body: 'Upload a brand PDF and the pages and products are read from it. Photo, video and product books are built here too.',
    },
    noMatch: {
        title: 'No lookbooks match',
        body: 'Nothing matches the search and filters together.',
        action: 'Clear filters',
    },
    delete: {
        title: (name: string) => `Delete ${name}?`,
        /** One sentence per kind of book (ALS-312): what goes with it, and what stays. */
        body: (family: LookbookFamily, content: number, productCount: number) =>
            `The book leaves the app and clients can no longer see it. ${deleteFate(family, content, productCount)} You can still find it here under Active: No.`,
        confirm: 'Delete lookbook',
        cancel: 'Cancel',
        failed: 'The lookbook could not be deleted. Try again, or cancel.',
        done: (name: string) => `Deleted · ${name}`,
    },
    filters: {
        all: 'All',
        /** The drawer's one group row. */
        groupTitle: 'Lookbook filters',
        groupSubtitle: 'Category, customer, dates & status',
        type: 'Type',
        category: 'Category',
        /** The prototype's label for the filter the URL keys as `client`. */
        client: 'Customer',
        colour: 'Colour group',
        active: 'Active',
        tag: 'Tag',
        createdBy: 'Created by',
        revisedBy: 'Revised by',
        effective: 'Effective',
        families: {
            Pages: 'Pages',
            Photos: 'Photos',
            Video: 'Video',
            Products: 'Products',
        },
    },
    record: {
        breadcrumb: 'LOOKBOOKS',
        newTitle: 'New lookbook',
        newSubtitle: 'The category decides what the book holds',
        create: 'Save lookbook',
        saveChanges: 'Save changes',
        cancel: 'Cancel',
        delete: 'Delete lookbook',
        subtitleFallback: 'Created',
        audit: (
            createdBy: string | null,
            createdOn: string | null,
            revisedBy: string | null,
            revisedOn: string | null,
        ) => {
            const createdDate = auditDate(createdOn);
            const revisedDate = auditDate(revisedOn);
            const created = `Created by ${createdBy ?? 'Unknown'}${createdDate ? ` on ${createdDate}` : ''}`;
            const revised = revisedBy
                ? `Revised by ${revisedBy}${revisedDate ? ` on ${revisedDate}` : ''}`
                : null;
            return revised ? `${created} · ${revised}` : created;
        },
        notFound: {
            title: 'This lookbook could not be found',
            body: 'It may have been deleted, or the link is wrong.',
            action: 'Back to lookbooks',
        },
        failed: {
            title: 'This lookbook could not be loaded',
            body: 'Try again, or come back later.',
        },
        discard: {
            title: 'Discard this lookbook?',
            cancel: 'Keep editing',
            confirm: 'Discard',
        },
    },
    details: {
        cardTitle: 'Lookbook details',
        cardCount: 'shown in the client app',
        name: 'Lookbook name',
        namePlaceholder: 'e.g. Eid picks for Zeina',
        category: 'Category',
        categoryPlaceholder: 'Select…',
        effective: 'Effective date & time',
        effectivePlaceholder: 'dd-mmm-yyyy · hh:mm',
        orientation: 'Orientation',
        orientationHint: 'How the images are laid out in the app.',
        orientationPlaceholder: 'Select…',
        customer: 'Customer',
        customerPlaceholder: 'Select a client…',
        notify: 'Notify clients',
        notifyCaption: 'Notification',
        active: 'Live in the app',
        activeCaption: 'Active',
        message: 'Message',
        messagePlaceholder: 'A short note the client sees with the book…',
        /** Per kind (screens/lookbook-new.html renderCover()); products has no cover at all. */
        cover: {
            pages: {
                label: 'Choose a page',
                note: (pageNumber: number) =>
                    `Page ${pageNumber} is the cover. Pick another page, or upload your own image.`,
            },
            photos: {
                label: 'Choose from photos',
                /** No photo carries a star to pick another with (ALS-399): the way back is the dialog's Photo 1 tile. */
                note: 'Photo 1 is the cover, or upload your own image.',
                /** lbn-cover__tag on a photo book's cover (renderCover()). */
                tag: 'Photo 1',
                /** The cover dialog's way back from an image of your own (ALS-348). */
                backToDefault: 'Use photo 1',
                /** What the dialog's footer names it: "Photo 1 will be the cover" (ALS-399). */
                name: 'Photo 1',
            },
            video: {
                label: 'Choose a still',
                note: 'A still from the first video, or upload your own image.',
                tag: 'Still',
                backToDefault: 'Use the still',
                name: 'The still',
            },
            /** ALS-399: the card once an image of the admin's own is the cover, with the way back. */
            ownTag: 'Your image',
            ownNote: (label: string) =>
                `Your own image is the cover. ${label} to change it or remove it.`,
        },
        required: 'Required.',
    },
    /**
     * Changing a new book's category while it holds content of another kind (Sanjin, ALS-79 QA).
     * The prototype switches without asking; this says what Save leaves out. The content stays
     * in the draft, so switching back brings it back.
     */
    switchCategory: {
        title: (category: string) => `Switch to ${category}?`,
        body: (content: string, count: number, kind: string) =>
            `${content} ${count === 1 ? 'is' : 'are'} not saved with a ${kind} book. Switch back before you save to keep ${count === 1 ? 'it' : 'them'}.`,
        keep: (category: string) => `Keep ${category}`,
        confirm: 'Switch',
        kind: { pages: 'pages', products: 'product', photos: 'photo', video: 'video' },
        products: (n: number) => `The ${products(n)} picked here`,
        pages: (n: number) => `The ${pages(n)} read from the PDF`,
        pdf: 'The PDF being read',
        photos: (n: number) => `The ${photos(n)} added here`,
        video: (n: number) => `The ${videos(n)} added here`,
    },
    /** "Cover image" (screens/lookbook-new.html's cover picker), the pages kind's own "Choose a page" dialog. */
    coverDialog: {
        title: 'Cover image',
        description: 'The image clients see for this lookbook in the app',
        fromPdf: 'Use a page from the PDF',
        pageCount: (count: number) => (count === 1 ? '1 page' : `${count} pages`),
        showing: (shown: number, total: number) => `Showing ${shown} of ${total} pages.`,
        noPages: 'No uploaded pages to choose from',
        uploadOwn: 'Upload your own',
        orUploadOwn: 'Or upload your own',
        dropLabel: 'Drop an image here or browse',
        dropNote: 'JPG or PNG · Portrait works best · Max 10 MB',
        uploading: (fileName: string) => `Uploading ${fileName}…`,
        uploadFailed: 'This image could not be uploaded. Try again.',
        /** ALS-399: the footer names what is selected, not the cover in use. */
        willBeCover: (name: string) => `${name} will be the cover`,
        ownImage: 'Your own image',
        replaceLabel: 'Replace with another image, or browse',
        remove: 'Remove',
        removeLabel: (fileName: string) => `Remove ${fileName}`,
        useOwn: (fileName: string) => `Use ${fileName} as the cover`,
        newUpload: 'new',
        cancel: 'Cancel',
        confirm: 'Use this image',
        current: 'Current',
        page: (pageNumber: number) => `Page ${pageNumber}`,
    },
    kinds: {
        pages: {
            cardTitle: 'Pages',
            cardCount: 'read from the PDF you upload',
            dropLabel: 'Drop the brand PDF here or browse',
            dropNote: 'PDF · up to 200 pages · pages and products read automatically',
            emptyBody:
                'If the PDF carries product links, the products on each page are read and can be created in bulk. If it does not, the pages come in as images.',
            /** ALS-80: the upload itself - real progress, a file accepted, or a failure to try again from. */
            uploading: (fileName: string) => `Uploading ${fileName}…`,
            remove: 'Remove the PDF',
            tryAgain: 'Try again',
            notAPdf: 'Choose a PDF file.',
            /** Our own wording for a 413, in place of the backend's shared `DocumentUpload.TooLarge`
             * ("That file is too large.") - that string is also what every other upload feature
             * (comments, styling sessions, product images, brand logos, wardrobe photos, videos)
             * shows, so it is rewritten here rather than renamed at the source. */
            tooLarge: 'Your file exceeds the size limit.',
            uploadFailed: 'This file could not be uploaded.',
            /** ALS-83: reading the book once it is uploaded (screens/lookbook-new.html's `lbn-steps`). */
            steps: {
                Splitting: 'Splitting the PDF into pages',
                ReadingLinks: 'Reading product links from each page',
                MatchingCatalogue: 'Matching products against the catalogue',
            },
            stepDone: 'Done',
            opening: 'Opening the PDF…',
            /** A job no task has claimed yet: behind another book, or handed back by a deploy. */
            queued: 'Waiting its turn…',
            waiting: (percent: number) => `Waiting · ${percent}%`,
            reading: (percent: number) => `Reading · ${percent}%`,
            pageProgress: (done: number, total: number) => `Page ${done} of ${total}`,
            pageCount: (count: number) => (count === 1 ? '1 page' : `${count} pages`),
            splitFailed: 'This PDF could not be read. Remove it and upload it again.',
            /** Save while the split is still going, or after it failed, with no pages to keep. */
            stillReading: "Wait until the PDF's pages are in, or remove the PDF.",
            /** ALS-81: the pages table (scripts/screens/lookbook-new.js pageRows()). */
            table: {
                selectAll: 'Select all pages',
                selectPage: (n: number) => `Select page ${n}`,
                reorder: 'Reorder',
                page: 'Page',
                products: 'Products',
                status: 'Status',
                createdBy: 'Created by',
                revisedBy: 'Revised by',
                actions: 'Actions',
                preview: 'Preview page',
                previewPage: (n: number) => `Preview page ${n}`,
                edit: 'Edit page',
                editPage: (n: number) => `Edit page ${n}`,
                delete: 'Delete page',
                deletePage: (n: number) => `Delete page ${n}`,
                footer: (shown: number, total: number) =>
                    `Showing ${shown} of ${total} pages · drag a row to reorder`,
                grip: 'Drag to reorder',
                gripLabel: (name: string) => `Reorder ${name}. Arrow keys move it.`,
                revised: (name: string, day: string) => `${name} · ${day}`,
                /** An empty cell. The prototype draws an em dash; the organisation's style rule
                 * (style-no-em-dash) allows none, so it is the en dash. */
                none: '\u2013',
            },
            /** statusCell(): Not placed added by ALS-96. */
            status: {
                noProducts: 'No products',
                needsDetails: 'Needs details',
                notPlaced: 'Not placed',
                ready: 'Ready',
            },
            reordered: 'Reordered · pages · saved with the book',
            /** The bulk bar the card's head turns into while pages are selected. */
            selection: {
                selected: (n: number) => (n === 1 ? '1 page selected' : `${n} pages selected`),
                of: (total: number) => `of ${total}`,
                clear: 'Clear selection',
                delete: (n: number) => (n === 1 ? 'Delete 1 page' : `Delete ${n} pages`),
                warningTitle: 'Deleting a page also drops the products read from it',
                warningBody: (where: string, count: number, saved: boolean) =>
                    `${where} hold ${count} ${count === 1 ? 'product' : 'products'} between them. ` +
                    (saved
                        ? 'Those products are already in the catalogue and stay there; what goes is their link to these pages.'
                        : 'Nothing has reached the catalogue yet, so deleting here removes them from this upload only.'),
            },
            /** confirmDeletePages(): the copy changes once the products are in the catalogue. */
            deleteConfirm: {
                title: (n: number) =>
                    `Delete ${n === 1 ? '1 page' : `${n} pages`} from this lookbook?`,
                bodySaved: (count: number) =>
                    `The pages come out of the book. The ${count} ${count === 1 ? 'product' : 'products'} read from them are already in the catalogue and stay there - only the link to these pages goes.`,
                bodyUnsaved:
                    'The pages come out of the book and the products read from them are dropped with them. Nothing has reached the catalogue yet, so this affects this upload only.',
                productCount: (n: number) => (n === 1 ? '1 product' : `${n} products`),
                note: 'This cannot be undone',
                cancel: 'Keep the pages',
                confirm: (n: number) => (n === 1 ? 'Delete 1 page' : `Delete ${n} pages`),
            },
            /** The page drawer (scripts/screens/lookbook-page.js PROTO_LOOKBOOK_PAGE). */
            drawer: {
                title: (n: number, total: number) => `Page ${n} of ${total}`,
                previous: 'Previous',
                next: 'Next',
                name: 'Page name/number',
                replace: 'Replace image',
                replacing: 'Uploading…',
                createdBy: 'Created by',
                createdOn: 'Created on',
                revisedBy: 'Revised by',
                revisedOn: 'Revised on',
                note: 'The image comes from the PDF. Replace it only if the page needs a different crop for the app.',
                noImage: (n: number) => `Page ${n}`,
                tagged: (n: number) =>
                    n === 1 ? '1 product tagged on this page' : `${n} products tagged on this page`,
                /** ALS-96: after `tagged`, while any tag on the page has no place on it. */
                taggedUnplaced: (n: number) => ` · ${n} not placed`,
                applies: 'Changes apply when the whole set is saved',
                cancel: 'Cancel',
                delete: 'Delete page',
                save: 'Save page',
                replaced: (n: number, file: string) =>
                    `Page ${n} image replaced with ${file} · applies when the set is saved`,
                replaceFailed: 'This image could not be uploaded.',
                you: 'You',
                /** ALS-82: the products tagged on the page (lookbook-page.js productRow() and render()). */
                tags: {
                    title: 'Product tags',
                    ready: (n: number) => `${n} ready`,
                    needs: (n: number) => `${n} need details`,
                    skipped: (n: number) => `${n} skipped`,
                    skippedTitle: 'Pieces the client already owns are not created',
                    search: 'Search products…',
                    searchLabel: 'Search the products on this page',
                    noMatch: 'No product on this page matches that.',
                    none: 'No products were read from this page. It comes into the app as an image.',
                    badge: {
                        ready: 'Ready',
                        incomplete: 'Incomplete',
                        skipped: 'Skipped',
                        reading: 'Reading',
                        notPlaced: 'Not placed',
                    },
                    /** ALS-96: the Incomplete badge's tooltip ends with what to do. */
                    incompleteHint: 'Add the cost price, supplier and tax rate.',
                    notPlaced: (n: number) => `${n} not placed`,
                    notPlacedTitle: 'The app has nowhere to draw these on the page',
                    /** ALS-366: the drawer's count of the page's products already in the catalogue. */
                    linked: (n: number) => `· ${n} already in the catalogue`,
                    linkedTitle: 'Products that already exist: nothing new is created for them',
                    /** ALS-366: a linked row's line, "[Brand] · In the catalogue", and its tooltip. */
                    inCatalogue: 'In the catalogue',
                    inCatalogueTitle: (code: string, how: string) => `${code} · ${how}`,
                    linkedHow: {
                        [LinkedBy.Link]: 'matched by its link',
                        [LinkedBy.Suggestion]: 'linked from a suggestion',
                        [LinkedBy.Hand]: 'linked by hand',
                    } satisfies Record<LinkedBy, string>,
                    link: 'Link to a product in the catalogue',
                    linkLabel: (name: string) => `Link ${name} to a product in the catalogue`,
                    unlink: 'Create as a new product instead',
                    unlinkLabel: (name: string) => `Unlink ${name} and create it as a new product`,
                    linkedNotice: (name: string, code: string) =>
                        `${name} linked to ${code} · nothing new is created for it`,
                    unlinked: (name: string) =>
                        `${name} will be created as a new product when the set is saved`,
                    suggestion: {
                        label: (name: string) => `Possible match for ${name}`,
                        before: 'Possibly already in the catalogue: ',
                        after: (brand: string, code: string) =>
                            ` · ${brand} · ${code}. Use it instead of creating a new product?`,
                        use: 'Use this product',
                        no: 'Not this one',
                    },
                    archived: 'Archived',
                    edit: 'Edit product',
                    editLabel: (name: string) => `Edit ${name}`,
                    open: (name: string) => `Open ${name} in the catalogue`,
                    own: 'Client owns it',
                    ownLabel: (name: string) => `${name}: the client owns it`,
                    notOwn: 'Not owned',
                    notOwnLabel: (name: string) => `${name}: the client does not own it`,
                    remove: 'Remove tag',
                    removeLabel: (name: string) => `Remove ${name}`,
                    removeTitle: (name: string) => `Remove ${name} from this page?`,
                    removeBody: (page: number) =>
                        `The product stops being tagged on page ${page}. It has not reached the catalogue yet, so nothing else changes.`,
                    /** ALS-366: a row linked to the catalogue. */
                    removeLinkedBody: (page: number, code: string) =>
                        `The product stops being tagged on page ${page}. It is a catalogue product (${code}) and stays there as it is.`,
                    removeSavedBody: (page: number) =>
                        `The product stops being tagged on page ${page} when the set is saved. It stays in the catalogue.`,
                    keep: 'Keep it',
                    /** ALS-96: a piece listed without a link, and without a brand to go by. */
                    piece: (n: number) => `Piece ${n}`,
                    /** ALS-96: placing each tag on the page image (lookbook-page.js canvasMarkup() and the place handlers). */
                    place: {
                        pin: 'Place on the page',
                        pinLabel: (name: string) => `Place ${name} on the page`,
                        marker: 'Not placed on the page yet',
                        dot: (n: number, name: string) =>
                            `Tag ${n}, ${name}. Drag, or use the arrow keys, to move it.`,
                        /** "Click the page where <name> is · Esc cancels", the name in bold. */
                        hintBefore: 'Click the page where ',
                        hintAfter: ' is · Esc cancels',
                        idle: 'Drag a dot to move a tag',
                        noImage: 'Tags are placed on the page image',
                        placed: (name: string, page: number) =>
                            `${name} placed on page ${page} · applies when the set is saved`,
                        placedMiddle: (name: string, page: number) =>
                            `${name} placed in the middle of page ${page} · move it with the arrow keys`,
                        check: 'New image: check the dots. They keep their place from the old image.',
                        checkOk: 'They’re right',
                        unplacedOne: (name: string) => `${name} is not placed on the page`,
                        unplacedMany: (n: number) => `${n} products are not placed on the page`,
                        unplacedBody: (one: boolean) =>
                            `Use the pin on ${one ? 'its row' : 'each row'} and click the page where the product is. The app draws a dot there, and a tag without one has nowhere to go.`,
                    },
                    /** Why a row is short or skipped, in the words the admin would use (the prototype's `reason`). */
                    reasons: {
                        owned: 'The client already owns this piece, so it is styled into the look but not sold.',
                        archived: (code: string) => `Archived in the catalogue as ${code}`,
                        notRead: 'Its page has not been read yet',
                        readFailed: 'Its page could not be read',
                        /** ALS-96: the prototype's reason for a piece listed without a link. */
                        notLinked: 'The page listed this piece without a link',
                        missing: (fields: readonly string[]) =>
                            `No ${fields.length === 1 ? fields[0] : `${fields.slice(0, -1).join(', ')} or ${fields[fields.length - 1]}`}`,
                    },
                },
            },
            /**
             * ALS-82: the Edit product dialog (lookbook-page.js PROTO_LOOKBOOK_PRODUCT, frame
             * 2825:49913, Qty and Size dropped on 9 Oct 2026). The labels are the line editor's.
             */
            productDialog: {
                close: 'Close',
                name: 'Product name',
                brand: 'Brand',
                url: 'Product URL',
                description: 'Description',
                images: 'Images',
                imageCount: (n: number) => (n === 1 ? '1 image' : `${n} images`),
                addImages: 'Add images',
                noImages: 'No images. The app shows the page instead.',
                colour: 'Colour',
                colourPlaceholder: 'Select colour…',
                taxIncluded: 'Tax incl.',
                yes: 'Yes',
                no: 'No',
                tax: 'Tax',
                taxPlaceholder: 'Select tax…',
                country: 'Country of origin',
                countryPlaceholder: 'Select country…',
                category: 'Category',
                categoryPlaceholder: 'Select category…',
                secondary: 'Secondary category',
                secondaryNone: 'None',
                currency: 'Currency',
                salePrice: 'Sale price',
                costPrice: 'Cost price',
                supplier: 'Supplier',
                supplierPlaceholder: 'Select supplier…',
                brandPlaceholder: 'Select brand…',
                search: 'Search…',
                empty: 'Nothing matches.',
                required: 'Required',
                /** ALS-96, the 12 Oct design: why the product needs details, at the top while it does. */
                needsTitle: 'Needs details',
                needsBody: (reason: string | null) =>
                    `${reason ? `${reason}. ` : ''}Add the cost price, supplier and tax rate to save it.`,
                footTitle: 'Update product details',
                footBody: 'Nothing is saved to the catalogue until the whole set is saved',
                cancel: 'Cancel',
                save: 'Save changes',
                ready: 'Ready',
                incomplete: 'Incomplete',
                alertTitle: (missing: readonly string[]) =>
                    missing.length === 1
                        ? `${missing[0]} is required`
                        : `${missing.length} required fields are empty`,
                alertBody: (missing: readonly string[]) =>
                    `Fill in ${missing.join(', ')} before saving. Nothing is written to the catalogue until the whole set is saved.`,
            },
            /**
             * ALS-84: the products the PDF links to (scripts/screens/lookbook-new.js renderPages(),
             * chipRow() and needsBody()), and the read of their pages, whose cost is agreed first
             * (ADR 0045; the prototype reads them for free, so that sentence is Batch import's).
             */
            links: {
                noLinks: (pageCount: string) => `${pageCount} · no product links`,
                read: (pageCount: string, n: number, needs: number, unplaced = 0, linked = 0) =>
                    `${pageCount} · ${products(n)} · ${linked > 0 ? `${linked} already in the catalogue · ` : ''}${needs > 0 ? `${needs} need details` : unplaced > 0 ? `${unplaced} not placed` : 'all ready'}`,
                inCatalogue: (pageCount: string, n: number) =>
                    `${pageCount} · ${products(n)} in the catalogue`,
                noLinksTitle: 'This PDF carries no product links',
                noLinksBody: (count: number) =>
                    `All ${count} pages are in and the book is ready to publish. Clients can browse it, but they cannot shop from it, since there are no products to link.`,
                needsTitle: (needs: number, total: number) =>
                    `${needs} of ${products(total)} ${total === 1 ? 'needs' : 'need'} details before anything is saved`,
                needsBody:
                    'The PDF gave no cost price, supplier or tax rate for these. Fill them in, then save the whole set in one go. Nothing reaches the catalogue until then.',
                /** ALS-366: "94 new products written to the catalogue" once some were linked, not created. */
                savedTitle: (n: number, linked = 0) =>
                    linked > 0
                        ? `${n} new ${n === 1 ? 'product' : 'products'} written to the catalogue`
                        : `${products(n)} written to the catalogue`,
                savedBody: (skipped: number, linked = 0) =>
                    (linked > 0
                        ? `${linked} more ${linked === 1 ? 'was' : 'were'} already in it and ${linked === 1 ? 'was' : 'were'} linked, not created again. All are tagged on the pages they came from.`
                        : 'They are linked to the pages they came from.') +
                    (skipped > 0
                        ? ` The ${skipped} the client already owns stayed in the look and were not created.`
                        : ''),
                saved: (n: number, skipped: number, linked = 0) =>
                    `${products(n)} written to the catalogue and linked to their pages` +
                    (linked > 0 ? ` · ${linked} already in it, linked` : '') +
                    (skipped > 0 ? ` · ${skipped} skipped, the client already owns them` : ''),
                refused: (needs: number) =>
                    `${products(needs)} still ${needs === 1 ? 'needs' : 'need'} a cost price, supplier or tax rate · nothing has been saved`,
                stillReading: 'Wait until the product pages are read.',
                /** ALS-82: the refusal names the product and its page, as the server's does. */
                refusedNamed: (name: string, page: string, more: number) =>
                    `${name} on ${page} needs details` +
                    (more > 0 ? ` and ${more} more` : '') +
                    ' · nothing has been saved',
                /** A link to a product archived in the catalogue: never revived nor made again by the save. */
                archivedOne: (name: string, code: string) =>
                    `${name} is archived in the catalogue as ${code}. Skip it, or restore the product first.`,
                archivedMany: (n: number) =>
                    `${n} products are archived in the catalogue. Skip them, or restore them first.`,
                chips: {
                    ready: (n: number) => `${n} ready`,
                    needs: (n: number) => `${n} need details`,
                    skipped: (n: number) => `${n} skipped`,
                    skippedTitle: 'Pieces the client already owns are not created',
                    notPlaced: (n: number) => `${n} not placed`,
                    notPlacedTitle: 'Tags with no place on their page',
                    /** ALS-366: where the products come from, beside the four that add up. */
                    linked: (linked: number, toCreate: number) =>
                        `· ${linked} already in the catalogue, ${toCreate} to create`,
                    linkedTitle: 'Products that already exist: nothing new is created for them',
                },
                /**
                 * ALS-96: a new tag with no place on its page holds the save. The prototype's body says
                 * the PDF gave no link; here every product has its link, and what is missing is the
                 * place, from a PDF read before tags had one, so the body says that.
                 */
                unplacedTitle: (n: number) =>
                    n === 1
                        ? '1 product is not placed on its page'
                        : `${n} products are not placed on their pages`,
                unplacedBody: (n: number) =>
                    `The PDF gave no place for ${n === 1 ? 'it' : 'them'} on the page, so there was nowhere to put the tag. Open the page and click where the product is; the app draws a dot there.`,
                refusedUnplaced: (n: number) =>
                    n === 1
                        ? '1 product is not placed on its page · nothing has been saved'
                        : `${n} products are not placed on their pages · nothing has been saved`,
                save: (n: number) => `Save ${products(n)}`,
                preview: 'Preview lookbook',
                /** The read of the pages the catalogue lacks: Batch import's words for its cost. */
                toRead: {
                    title: (n: number) =>
                        n === 1
                            ? '1 product is not in the catalogue yet'
                            : `${n} products are not in the catalogue yet`,
                    body: 'Their pages are read for a name, brand, price and category, the way Batch import reads them. What a page cannot give is filled in before the book is saved.',
                    checking: 'Checking the cost…',
                    cost: (count: number, cost: string) =>
                        `Reading ${count} ${count === 1 ? 'page' : 'pages'} costs ${cost}`,
                    agree: (cost: string) => `I understand this costs ${cost}.`,
                    start: (count: number) =>
                        count === 1 ? 'Read 1 product page' : `Read ${count} product pages`,
                    failed: 'The cost could not be checked - try again in a moment.',
                    startFailed: 'The product pages could not be read - try again in a moment.',
                    noCredits: 'Not enough reading credits left this month for this book.',
                    off: 'Product pages cannot be read here, so these products need their details filled in.',
                    progress: (done: number, total: number) =>
                        `${done} of ${total} product pages read`,
                },
            },
            /** The whole book in the document overlay, as the client opens it. */
            bookPreview: {
                title: 'Lookbook preview',
                /** doc-preview.js's NOTES.lookbook, its first sentence: the rest describes the prototype. */
                note: 'The book as the client opens it.',
                subtitle: (pageCount: string, pdfName: string | null) =>
                    pdfName ? `${pageCount} · ${pdfName}` : pageCount,
            },
            /** The page eye opens the page itself in the document overlay. */
            preview: {
                /** doc-preview.js's NOTES.page. */
                note: 'The page as the client sees it, read from the PDF.',
                print: 'Print',
                subtitle: (n: number, total: number, pdfName: string | null) =>
                    pdfName ? `Page ${n} of ${total} · ${pdfName}` : `Page ${n} of ${total}`,
                /** ALS-96: doc-preview.js's tags toggle, off when it opens, as in the app. */
                showTags: 'Show tags',
                hideTags: 'Hide tags',
            },
        },
        /** ALS-79: the media card's vocabulary per kind (scripts/screens/lookbook-new.js `MEDIA`). */
        photos: {
            cardTitle: 'Photos',
            cardCount: 'shown as a gallery in the app',
            one: 'photo',
            many: 'photos',
            count: (n: number) => photos(n),
            add: 'Add photos',
            dropLabel: 'Drop the photos here or browse',
            limits: 'JPG or PNG · up to 40 images · max 10 MB each',
            /** 2879:2891 prints nothing under the photo grid. */
            note: '',
            dimensionsOrLength: 'Dimensions',
        },
        video: {
            cardTitle: 'Videos',
            cardCount: 'shown in the app as a film',
            one: 'video',
            many: 'videos',
            count: (n: number) => videos(n),
            add: 'Add videos',
            dropLabel: 'Drop the videos here or browse',
            limits: 'MP4 or MOV · up to 4 films · max 500 MB each',
            /** 2895:4474 */
            note: 'The link under each video is where the client goes when they tap it. Edit a video to set it.',
            dimensionsOrLength: 'Length',
        },
        /** ALS-79: what the photo and video cards share, by kind where the words differ. */
        media: {
            dragToReorder: (count: string) => `${count} · drag to reorder`,
            reordered: (count: string) => `Reordered · ${count} · saved with the book`,
            added: (count: string) => `${count} added`,
            uploading: (fileName: string) => `Uploading ${fileName}…`,
            cancel: 'Cancel',
            /** Refused before anything goes up the wire. */
            notAPhoto: (fileName: string) => `${fileName} is not a JPG or PNG photo.`,
            notAVideo: (fileName: string) => `${fileName} is not an MP4 or MOV video.`,
            tooLarge: (fileName: string, limit: string) =>
                `${fileName} is over the ${limit} limit.`,
            full: (count: string) => `The book already holds ${count}, as many as it can.`,
            uploadFailed: (fileName: string) => `${fileName} could not be uploaded.`,
            /** Save while a file is still going up. */
            stillUploading: 'Wait for the uploads to finish before saving.',
            needsOne: (one: string) =>
                `A ${one} book needs at least one file before it can be saved`,
            /** After Download on a selection: the prototype's "N files requested", without its prototype-only aside. */
            downloadRequested: (n: number) =>
                n === 1 ? '1 file requested' : `${n} files requested`,
            dismiss: 'Dismiss',
            downloadFailed:
                'The file could not be downloaded. Allow pop-ups for this site and try again.',
            downloadsFailed: (n: number) =>
                n === 1 ? '1 file could not be downloaded.' : `${n} files could not be downloaded.`,
            selection: {
                selected: (count: string) => `${count} selected`,
                of: (n: number) => `of ${n}`,
                clear: 'Clear selection',
                download: 'Download',
                delete: (count: string) => `Delete ${count}`,
            },
            tile: {
                preview: (fileName: string) => `Preview ${fileName}`,
                select: (fileName: string) => `Select ${fileName}`,
                edit: 'Edit video',
                editFile: (fileName: string) => `Edit ${fileName}`,
                download: 'Download',
                downloadFile: (fileName: string) => `Download ${fileName}`,
                delete: 'Delete',
                deleteFile: (fileName: string) => `Delete ${fileName}`,
                grip: 'Drag to reorder',
                gripLabel: (fileName: string) => `Reorder ${fileName}. Arrow keys move it.`,
                noLink: '–',
                /** A legacy file still on the old portal's server, which this admin cannot show. */
                onOldServer: 'Not copied from the old portal yet',
            },
            table: {
                selectAll: 'Select all files',
                reorder: 'Reorder',
                image: 'Image',
                file: 'File',
                size: 'Size',
                order: 'Order',
                actions: 'Actions',
                footer: (shown: number, total: number, many: string) =>
                    `Showing ${shown} of ${total} ${many} · drag a row to reorder`,
            },
            preview: {
                photoSubtitle: (dimensions: string, size: string, n: number, total: number) =>
                    [dimensions, size, `photo ${n} of ${total}`].filter(Boolean).join(' · '),
                videoSubtitle: (length: string, size: string, link: string) =>
                    [length, size, link ? `opens ${link}` : ''].filter(Boolean).join(' · '),
                download: 'Download',
                /** doc-preview.js's NOTES.photo and NOTES.video, without the prototype's own aside. */
                photoNote: 'The photograph as the client sees it in the gallery.',
                videoNote: 'The film as it plays in the app.',
            },
            delete: {
                title: (count: string) => `Delete ${count} from this lookbook?`,
                body: 'They come out of the book and out of the app. The original files are not kept here, so download anything worth keeping first.',
                note: 'This cannot be undone',
                cancel: 'Keep them',
                confirm: (count: string) => `Delete ${count}`,
            },
            editVideo: {
                title: 'Edit video',
                link: 'Link',
                linkHint:
                    'Where the client goes when they tap the video. Leave it empty and the video just plays.',
                linkPlaceholder: 'https://…',
                fromTheFile: 'From the file',
                file: 'File',
                length: 'Length',
                size: 'Size',
                order: 'Order',
                orderOf: (n: number, total: number) => `${n} of ${total}`,
                cancel: 'Cancel',
                save: 'Save changes',
            },
        },
        /** The grid / list switch every book card with a choice of view shares (ALS-79, ALS-355). */
        views: {
            grid: 'Grid view',
            list: 'List view',
        },
        products: {
            cardTitle: 'Products',
            cardCount: 'picked from the catalogue',
            emptyTitle: 'No products yet',
            emptyBody:
                'Pick products from the catalogue. A product has to exist before it can go in a book, so anything missing is created in Products first.',
            /** ALS-78: the picked tiles and what each one can do; the grid and list from ALS-355. */
            pick: 'Pick products',
            count: (n: number) => `${products(n)} · drag to reorder`,
            explainer:
                'Products come from the catalogue. If one is missing, create it in Products first, then pick it here.',
            noColour: 'No colour',
            noLink: '-',
            productPage: 'Product page',
            edit: (name: string) => `Edit ${name}`,
            editTitle: 'Edit product',
            remove: (name: string) => `Remove ${name}`,
            removeTitle: 'Remove from book',
            grip: 'Drag to reorder',
            gripLabel: (name: string) => `Reorder ${name}. Arrow keys move it.`,
            /** ALS-355: a product archived since it was added (lookbook-new.js isArchived()). */
            archived: 'Archived',
            archivedTitle:
                'Archived in the catalogue: it stays in this book but is not offered for new picks',
            table: {
                reorder: 'Reorder',
                position: '#',
                product: 'Product',
                colour: 'Colour',
                link: 'Link',
                price: 'Price',
                actions: 'Actions',
            },
            /** The prototype's refusal, verbatim (lookbook-new.js), and the server's (LookbookProducts.cs). */
            needsOne: 'A product book needs at least one product before it can be saved.',
        },
    },
    /** "Pick products" (scripts/screens/lookbook-picker.js), the product book's catalogue picker. */
    productPicker: {
        title: 'Pick products',
        description: 'From the catalogue. A product has to exist before it can go in a book.',
        searchLabel: 'Search the catalogue',
        searchPlaceholder: 'Search by name, brand or SKU…',
        brandLabel: 'Brand',
        allBrands: 'All brands',
        categoryLabel: 'Category',
        allCategories: 'All categories',
        alreadyInBook: 'already in this book',
        empty: 'No products match.',
        showing: (shown: number, total: number) =>
            `Showing ${shown.toLocaleString('en-GB')} of ${products(total)}`,
        footerNote:
            'A product that is not here has to be created in Products first. It cannot be created from this modal.',
        selected: (n: number) => `${n.toLocaleString('en-GB')} selected`,
        add: (n: number) => (n === 0 ? 'Add products' : `Add ${products(n)}`),
        cancel: 'Cancel',
        failed: 'The products could not be added. Try again.',
        /** ALS-366: one pick, to link a product a lookbook PDF read to one the catalogue has. */
        single: {
            title: 'Link to a product in the catalogue',
            description: (name: string | null) =>
                `Use a product that already exists instead of creating ${name ?? 'this one'}.`,
            footerNote:
                'Nothing new is created for this tag; it points at the product you pick, in the colour you choose. Unlink it later to create it as a new product after all.',
            add: 'Link to this product',
            colourLabel: (name: string) => `Colour of ${name}`,
            noColour: 'No colour',
        },
    },
    /** "Edit product" (scripts/screens/lookbook-picker.js): one tile's product, colour and link. */
    editProduct: {
        title: 'Edit product',
        description:
            'How this product appears in the book. The product record itself lives in Products.',
        product: 'Product',
        productMessage: 'Search the catalogue to swap it for another product.',
        change: 'Change product',
        keep: 'Keep this product',
        searchLabel: 'Search the catalogue',
        searchPlaceholder: 'Search by name, brand or SKU…',
        empty: 'No products match.',
        alreadyInBook: 'already in this book',
        /** ALS-355: the item's own product, archived since it was added (lookbook-picker.js). */
        archived: 'Archived',
        archivedSuffix: ' · Archived',
        showing: (shown: number, total: number) =>
            `Showing ${shown.toLocaleString('en-GB')} of ${products(total)}`,
        colour: 'Colour',
        colourMessage: 'Which colourway the client sees in the book.',
        /** ALS-355: ours, not the prototype's, which lets the colour change; Save would refuse it. */
        colourLocked: 'An archived product keeps its colour. Change the product to choose another.',
        colourPlaceholder: 'Select a colour…',
        noColours: 'This product has no colours',
        link: 'Link',
        linkMessage:
            'Where the client goes when they tap it. Leave it empty to use the product page.',
        linkPlaceholder: 'https://…',
        /** The server's own messages (LookbookContracts.cs), so a refusal reads the same either side. */
        linkNotWeb: 'A link must be a full web address, starting http:// or https://.',
        linkTooLong: (max: number) => `A link can be at most ${max} characters.`,
        fromCatalogue: 'From the catalogue',
        sku: 'SKU',
        price: 'Price',
        supplier: 'Supplier',
        position: (n: number, of: number) => `Position ${n} of ${of}`,
        positionLabel: 'In this book',
        cancel: 'Cancel',
        save: 'Save changes',
    },
    /** Both cards' dropzones are inert past the click/drag interaction itself - actually reading a file is the separate upload ticket. */
    uploadsComingSoon: 'Files are accepted here, but reading them lands with a follow-up ticket.',
    attachments: {
        cardTitle: 'Comment & attachments',
        cardCount: 'internal, never shown to the client',
        commentPlaceholder: 'Add a note for the team…',
        dropLabel: 'Drag files here or browse',
        dropNote: 'PDF, JPG or PNG · max 10 MB each',
    },
    /**
     * In the app (ALS-74). Ours: no frame draws it. Says where the book stands in the client app by
     * the app's own rules, what it will leave out, and how clients have opened it.
     */
    inApp: {
        title: 'In the app',
        loading: 'Checking the app',
        failed: 'Where this book stands in the app could not be read.',
        state: {
            Live: 'Live',
            Scheduled: 'Scheduled',
            Off: 'Not in the app',
            Empty: 'Not in the app',
        },
        liveSince: (when: string) => `In the app since ${when}.`,
        goesLive: (when: string) => `Goes live in the app on ${when}.`,
        switchedOff: 'Live in the app is switched off, so clients do not see it.',
        nothingToShow:
            'The app lists a book only once it has something to show (a page, a photo, a video or a product with a picture), so clients do not see it yet.',
        onlyFor: (name: string) => `Only ${name} sees it.`,
        gapsTitle: 'The app will show it with gaps',
        gapsTitleNotInApp: 'The app would show it with gaps',
        opened: (clients: number, views: number, first: string, last: string) =>
            `Opened by ${clients} ${clients === 1 ? 'client' : 'clients'} · ${views} page ${views === 1 ? 'view' : 'views'} · first ${first}, last ${last}.`,
        notOpened: 'No client has opened it in the app yet.',
        notLogged: 'The app does not record clients opening a product book.',
        notify: 'Notify clients sends nothing yet: no push is sent for a lookbook until the app contract is agreed.',
    },
} as const;
